import { useCallback, useEffect, useRef } from 'react';
import paper from 'paper';
import {ANGLE_COEFFICIENT, FULL_LARGE_GLASS_SIZE, GLASS_PRESETS, MM_TO_PX} from '@/constants/editor';



export function useBackgroundCanvas({
  showGrid,
  glassType,
  showGuides = true,
  showCrosshair = true,
  activeSnap = {x: false, y: false},
  customGuides = [],
  theme = 'dark'
}) {
  const backgroundCanvasRef = useRef(null);
  const showGridRef = useRef(showGrid);
  const glassTypeRef = useRef(glassType);
  const showGuidesRef = useRef(showGuides);
  const showCrosshairRef = useRef(showCrosshair);
  const customGuidesRef = useRef(customGuides);
  const themeRef = useRef(theme);

  const activeSnapRef = useRef(activeSnap);

  useEffect(() => {
    showGridRef.current = showGrid;
  }, [showGrid]);

  useEffect(() => {
    glassTypeRef.current = glassType;
  }, [glassType]);

  useEffect(() => {
    showGuidesRef.current = showGuides;
  }, [showGuides]);

  useEffect(() => {
    showCrosshairRef.current = showCrosshair;
  }, [showCrosshair]);

  useEffect(() => {
    activeSnapRef.current = activeSnap;
  }, [activeSnap]);

  useEffect(() => {
    customGuidesRef.current = customGuides;
  }, [customGuides]);

  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  const drawCustomGuides = useCallback((ctx, zoom, bounds, width, height) => {
    if (!showGuidesRef.current) return;
    const guides = customGuidesRef.current;
    if (!guides || guides.length === 0) return;

    const snap = activeSnapRef.current;
    const snappedIds = new Set([
      ...(snap.customX || []), 
      ...(snap.customY || []),
      ...(snap.customInclined || [])
    ]);

    ctx.save();

    guides.forEach((guide, index) => {
      const {x, y, angle = 0, id} = guide;
      const isSnapped = snappedIds.has(id);

      const screenX = (x - bounds.left) * zoom;
      const screenY = (y - bounds.top) * zoom;

      ctx.beginPath();
      ctx.strokeStyle = isSnapped ? '#10b981' : (themeRef.current === 'dark' ? '#06b6d4' : '#0891b2');
      ctx.lineWidth = isSnapped ? 1.5 : 1;
      ctx.setLineDash(isSnapped ? [] : [6, 6]);

      const rad = (angle * Math.PI) / ANGLE_COEFFICIENT;
      const dx = Math.cos(rad);
      const dy = Math.sin(rad);

      const length = Math.max(width, height) * 2;
      ctx.moveTo(screenX - dx * length, screenY - dy * length);
      ctx.lineTo(screenX + dx * length, screenY + dy * length);
      ctx.stroke();

      ctx.setLineDash([]);
      ctx.globalAlpha = 0.8;

      ctx.beginPath();
      ctx.arc(screenX, screenY, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#06b6d4';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();

      const handleOffset = 40;
      const hx = screenX + dx * handleOffset;
      const hy = screenY + dy * handleOffset;

      ctx.beginPath();
      ctx.arc(hx, hy, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#f59e0b';
      ctx.fill();
      ctx.stroke();

      ctx.save();
      ctx.fillStyle = themeRef.current === 'dark' ? '#fde68a' : '#92400e';
      ctx.font = '14px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      
      const displayAngle = ((angle % 360) + 360) % 360;
      const angleText = `${displayAngle.toFixed(2)} °`;
      
      ctx.fillText(angleText, hx + 24, hy);
      ctx.restore();

      const dhx = screenX - dx * handleOffset;
      const dhy = screenY - dy * handleOffset;

      ctx.beginPath();
      ctx.arc(dhx, dhy, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      ctx.stroke();

      const ihx = screenX - dx * handleOffset * 2;
      const ihy = screenY - dy * handleOffset * 2;

      ctx.beginPath();
      ctx.arc(ihx, ihy, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#10b981';
      ctx.fill();
      ctx.stroke();
    });

    ctx.restore();
  }, []);

  const drawGlassRings = useCallback((ctx, zoom, bounds, width, height) => {
    const currentGlass = glassTypeRef.current;
    if (currentGlass === 'none') return;

    const preset = GLASS_PRESETS[currentGlass] || GLASS_PRESETS[FULL_LARGE_GLASS_SIZE];
    const { outerR, innerR } = preset;

    const outerRPx = outerR * MM_TO_PX;
    const innerRPx = innerR * MM_TO_PX;

    const centerX = (0 - bounds.left) * zoom;
    const centerY = (0 - bounds.top) * zoom;

    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    ctx.beginPath();
    ctx.arc(centerX, centerY, outerRPx * zoom, 0, Math.PI * 2);
    ctx.strokeStyle = themeRef.current === 'dark' ? '#38bdf8' : '#0284c7';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = themeRef.current === 'dark' ? 'rgba(56, 189, 248, 0.03)' : 'rgba(2, 132, 199, 0.02)';
    ctx.fill();

    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.arc(centerX, centerY, innerRPx * zoom, 0, Math.PI * 2);
    ctx.strokeStyle = themeRef.current === 'dark' ? '#f59e0b' : '#ea580c';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);
  }, []);

  const drawCentralCrosshair = useCallback((ctx, zoom, bounds, width, height) => {
    if (!showCrosshairRef.current) return;

    const centerX = (0 - bounds.left) * zoom;
    const centerY = (0 - bounds.top) * zoom;
    const snap = activeSnapRef.current;

    ctx.save();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    const crosshairColor = themeRef.current === 'dark' ? '#22d3ee' : '#0891b2';

    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.strokeStyle = snap.y ? '#10b981' : crosshairColor;
    ctx.lineWidth = 1.5;
    ctx.setLineDash(snap.y ? [] : [6, 6]);
    ctx.globalAlpha = snap.y ? 1 : 0.85;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, height);
    ctx.strokeStyle = snap.x ? '#10b981' : crosshairColor;
    ctx.lineWidth = 1.5;
    ctx.setLineDash(snap.x ? [] : [6, 6]);
    ctx.globalAlpha = snap.x ? 1 : 0.85;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(centerX, centerY, snap.x || snap.y ? 1.5 : 2, 0, Math.PI * 2);
    ctx.fillStyle = snap.x || snap.y ? '#10b981' : crosshairColor;
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.fill();

    ctx.restore();
  }, []);

  const drawBackgroundCanvas = useCallback(() => {
    const canvas = backgroundCanvasRef.current;
    if (!canvas || !paper.view) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);

    const zoom = paper.view.zoom;
    const bounds = paper.view.bounds;
    if (!zoom || zoom <= 0 || !bounds || isNaN(bounds.left)) return;

    ctx.setLineDash([]);

    if (showGridRef.current) {
      const stepMain = 100;
      const stepMedium = 20;
      const stepFine = 4;
      const stepExtraFine = 1;

      const mediumOpacity = Math.min(Math.max((zoom - 0.3) * 1.5, 0), 0.6);
      const fineOpacity = Math.min(Math.max((zoom - 0.9) * 0.8, 0), 0.4);
      const extraFineOpacity = Math.min(Math.max((zoom - 2.5) * 0.5, 0), 0.25);

      let activeStep = stepMain;
      if (zoom > 2.5) activeStep = stepExtraFine;
      else if (zoom > 0.9) activeStep = stepFine;
      else if (zoom > 0.3) activeStep = stepMedium;

      const startX = Math.floor(bounds.left / activeStep) * activeStep;
      const endX = Math.ceil(bounds.right / activeStep) * activeStep;
      const startY = Math.floor(bounds.top / activeStep) * activeStep;
      const endY = Math.ceil(bounds.bottom / activeStep) * activeStep;

      const getLineStyle = (val) => {
        const rounded = Math.abs(Math.round(val));
        const isDark = themeRef.current === 'dark';
        
        if (rounded % stepMain === 0) return { color: isDark ? '#b2c2da' : '#64748b', width: 1.2, opacity: 0.8 };
        if (rounded % stepMedium === 0) return { color: isDark ? '#99a8d1' : '#94a3b8', width: 0.8, opacity: mediumOpacity };
        if (rounded % stepFine === 0) return { color: isDark ? '#75bfe3' : '#94a3b8', width: 0.5, opacity: fineOpacity };
        return { color: isDark ? '#8a90d3' : '#cbd5e1', width: 0.35, opacity: extraFineOpacity };
      };

      const toScreen = (x, y) => ({
        x: (x - bounds.left) * zoom,
        y: (y - bounds.top) * zoom,
      });

      const drawLine = (p1, p2, style) => {
        if (style.opacity <= 0.02) return;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.strokeStyle = style.color;
        ctx.globalAlpha = style.opacity;
        ctx.lineWidth = style.width;
        ctx.stroke();
      };

      for (let x = startX; x <= endX; x += activeStep) {
        if (Math.abs(Math.round(x)) === 0) continue;
        const style = getLineStyle(x);
        drawLine(toScreen(x, bounds.top), toScreen(x, bounds.bottom), style);
      }
      for (let y = startY; y <= endY; y += activeStep) {
        if (Math.abs(Math.round(y)) === 0) continue;
        const style = getLineStyle(y);
        drawLine(toScreen(bounds.left, y), toScreen(bounds.right, y), style);
      }

      ctx.globalAlpha = 1;
    }

    drawGlassRings(ctx, zoom, bounds, width, height);
    drawCentralCrosshair(ctx, zoom, bounds, width, height);
    drawCustomGuides(ctx, zoom, bounds, width, height);
  }, [drawGlassRings, drawCentralCrosshair, drawCustomGuides]);

  useEffect(() => {
    if (!paper.view) return;

    drawBackgroundCanvas();

    const handleFrame = () => drawBackgroundCanvas();
    paper.view.on('frame', handleFrame);

    return () => {
      if (paper.view) paper.view.off('frame', handleFrame);
    };
  }, [showGrid, glassType, showGuides, showCrosshair, customGuides, theme, drawBackgroundCanvas]);

  return { backgroundCanvasRef, drawBackgroundCanvas };
}